import {
  Box,
  Text,
  Group,
  Select,
  ActionIcon,
  Menu,
  useMantineTheme,
} from '@mantine/core';
import { forwardRef, useState } from 'react';
import {
  IconDots,
  IconDownload,
  IconRefresh,
  IconMaximize,
  IconFilter,
} from '@tabler/icons-react';
import { Paper } from '../../ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  height?: number | string;
  loading?: boolean;
  error?: string;
  timeRanges?: { value: string; label: string }[];
  selectedTimeRange?: string;
  onTimeRangeChange?: (value: string) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  onExpand?: () => void;
  onFilter?: () => void;
  headerActions?: React.ReactNode;
  footerContent?: React.ReactNode;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  variant?: 'default' | 'elevated' | 'outlined';
}

const ChartCard = forwardRef<HTMLDivElement, ChartCardProps>(
  (
    {
      title,
      subtitle,
      children,
      height = 300,
      loading = false,
      error,
      timeRanges,
      selectedTimeRange,
      onTimeRangeChange,
      onRefresh,
      onExport,
      onExpand,
      onFilter,
      headerActions,
      footerContent,
      gradientVariant = 'primary',
      variant = 'default',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const defaultTimeRanges = [
      { value: '7d', label: 'Last 7 days' },
      { value: '30d', label: 'Last 30 days' },
      { value: '90d', label: 'Last 90 days' },
      { value: '1y', label: 'Last year' },
    ];

    const ranges = timeRanges || defaultTimeRanges;

    return (
      <Paper ref={ref} variant={variant} p={0}>
        {/* Header */}
        <Box p="md" style={{ borderBottom: `1px solid ${theme.colors.gray[2]}` }}>
          <Group justify="space-between" align="flex-start">
            <Box>
              <Text fw={600} size="lg" style={accessibilityStyles}>
                {title}
              </Text>
              {subtitle && (
                <Text size="sm" c="dimmed" style={accessibilityStyles}>
                  {subtitle}
                </Text>
              )}
            </Box>

            <Group gap="xs">
              {headerActions}

              {onTimeRangeChange && (
                <Select
                  size="xs"
                  w={130}
                  data={ranges}
                  value={selectedTimeRange}
                  onChange={(value) => onTimeRangeChange(value || '7d')}
                />
              )}

              <Menu shadow="md" width={160} position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="subtle">
                    <IconDots size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {onRefresh && (
                    <Menu.Item
                      leftSection={<IconRefresh size={14} />}
                      onClick={onRefresh}
                    >
                      Refresh
                    </Menu.Item>
                  )}
                  {onFilter && (
                    <Menu.Item
                      leftSection={<IconFilter size={14} />}
                      onClick={onFilter}
                    >
                      Filter
                    </Menu.Item>
                  )}
                  {onExpand && (
                    <Menu.Item
                      leftSection={<IconMaximize size={14} />}
                      onClick={onExpand}
                    >
                      Expand
                    </Menu.Item>
                  )}
                  {onExport && (
                    <>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<IconDownload size={14} />}
                        onClick={onExport}
                      >
                        Export
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Box>

        {/* Chart content */}
        <Box p="md" h={height} pos="relative">
          {loading && (
            <Box
              pos="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.8)',
                zIndex: 10,
              }}
            >
              <Text c="dimmed" style={accessibilityStyles}>Loading...</Text>
            </Box>
          )}

          {error && (
            <Box
              pos="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <Text c="red" mb="xs" style={accessibilityStyles}>{error}</Text>
              {onRefresh && (
                <ActionIcon variant="light" onClick={onRefresh}>
                  <IconRefresh size={16} />
                </ActionIcon>
              )}
            </Box>
          )}

          {!loading && !error && children}
        </Box>

        {/* Footer */}
        {footerContent && (
          <Box p="md" style={{ borderTop: `1px solid ${theme.colors.gray[2]}` }}>
            {footerContent}
          </Box>
        )}
      </Paper>
    );
  }
);

ChartCard.displayName = 'ChartCard';

export default ChartCard;
